(() => {
  const initialScope = document.currentScript?.parentElement;
  const rootSelector = '[data-product-details-enhancement]';
  const sourceSelector = '[data-product-details-source]';
  const unsupportedHeadingContent =
    'a, button, input, select, textarea, details, summary, [contenteditable="true"]';
  const supportedHeadingElements = new Set([
    'ABBR',
    'B',
    'BDI',
    'BDO',
    'BR',
    'CODE',
    'DATA',
    'EM',
    'I',
    'MARK',
    'S',
    'SMALL',
    'SPAN',
    'STRONG',
    'SUB',
    'SUP',
    'TIME',
    'WBR',
  ]);
  const meaningfulContentSelector =
    'img, picture, video, audio, iframe, table, hr, svg, canvas, object, embed';

  const hasSupportedHeadingContent = (heading) => {
    if (!heading.textContent.trim() || heading.querySelector(unsupportedHeadingContent)) return false;

    return Array.from(heading.querySelectorAll('*')).every((element) =>
      supportedHeadingElements.has(element.tagName),
    );
  };

  const createElement = (tagName, className) => {
    const element = document.createElement(tagName);
    element.className = className;
    return element;
  };

  const hasMeaningfulContent = (nodes) =>
    nodes.some(
      (node) =>
        (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) ||
        (node.nodeType === Node.ELEMENT_NODE &&
          (node.textContent.trim() ||
            node.matches(meaningfulContentSelector) ||
            node.querySelector(meaningfulContentSelector))),
    );

  const enhance = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.productDetailsEnhanced === 'true') return false;

    const source = root.querySelector(`:scope > ${sourceSelector}`);
    if (!source) return false;

    const childNodes = Array.from(source.childNodes);
    const directHeadings = childNodes.filter(
      (node) => node.nodeType === Node.ELEMENT_NODE && node.tagName === 'H3',
    );

    if (directHeadings.length === 0) return false;
    if (source.querySelector('h1, h2')) return false;
    if (source.querySelectorAll('h3').length !== directHeadings.length) return false;
    if (!directHeadings.every(hasSupportedHeadingContent)) return false;

    const firstHeadingIndex = childNodes.indexOf(directHeadings[0]);
    const overviewNodes = childNodes.slice(0, firstHeadingIndex);
    const overviewHasContent = overviewNodes.some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE ||
        (node.nodeType === Node.TEXT_NODE && node.textContent.trim()),
    );
    const overviewHasSubordinateHeading = overviewNodes.some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches('h4, h5') || node.querySelector('h4, h5')),
    );
    if (overviewHasSubordinateHeading) return false;

    const groups = directHeadings.map((heading, index) => {
      const start = childNodes.indexOf(heading) + 1;
      const nextHeading = directHeadings[index + 1];
      const end = nextHeading ? childNodes.indexOf(nextHeading) : childNodes.length;

      return { heading, bodyNodes: childNodes.slice(start, end) };
    });

    const normalizedLabels = directHeadings.map((heading) =>
      heading.textContent.trim().replace(/\s+/g, ' ').toLocaleLowerCase(),
    );

    if (firstHeadingIndex < 0 || groups.some(({ heading }) => !source.contains(heading))) return false;
    if (new Set(normalizedLabels).size !== normalizedLabels.length) return false;
    if (groups.some(({ bodyNodes }) => !hasMeaningfulContent(bodyNodes))) return false;

    const overview = createElement(
      'div',
      'product-details-prototype__overview product__description rte',
    );
    const sections = createElement('div', 'product-details-prototype__sections');
    const replacement = document.createDocumentFragment();
    const headingsWithoutLabelClass = directHeadings.filter(
      (heading) => !heading.classList.contains('product-details-disclosure__label'),
    );

    try {
      overviewNodes.forEach((node) => overview.append(node));

      groups.forEach(({ heading, bodyNodes }) => {
        const disclosure = createElement('details', 'product-details-disclosure');
        const summary = createElement('summary', 'product-details-disclosure__summary');
        const index = createElement('span', 'product-details-disclosure__index');
        const indicator = createElement('span', 'product-details-disclosure__indicator');
        const body = createElement(
          'div',
          'product-details-disclosure__body product__description rte',
        );

        index.setAttribute('aria-hidden', 'true');
        indicator.setAttribute('aria-hidden', 'true');
        heading.classList.add('product-details-disclosure__label');
        summary.append(index, heading, indicator);
        bodyNodes.forEach((node) => body.append(node));
        disclosure.append(summary, body);
        sections.append(disclosure);
      });

      if (overviewHasContent) {
        replacement.append(overview);
      } else {
        replacement.append(...overview.childNodes);
      }
      replacement.append(sections);
      source.replaceWith(replacement);
      root.dataset.productDetailsEnhanced = 'true';
      return true;
    } catch (error) {
      headingsWithoutLabelClass.forEach((heading) =>
        heading.classList.remove('product-details-disclosure__label'),
      );
      source.replaceChildren(...childNodes);
      return false;
    }
  };

  const init = (scope = document) => {
    if (scope instanceof Element && scope.matches(rootSelector)) enhance(scope);
    scope.querySelectorAll(rootSelector).forEach(enhance);
  };

  if (window.PeakProductDetails) {
    if (initialScope) window.PeakProductDetails.init(initialScope);
    return;
  }

  window.PeakProductDetails = { enhance, init };
  if (initialScope) init(initialScope);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
